import { Link } from "react-router-dom";
import { Phone, Mail, Globe, MapPin } from "lucide-react";
import { SITE, waLink } from "@/config/site";
import { WhatsAppIcon } from "@/components/icons";
import { track } from "@/lib/analytics";

const SERVICE_LINKS = [
  { label: "Rental Management", href: "/rental-management" },
  { label: "Rent Collection", href: "/rent-collection" },
  { label: "Property Inspection", href: "/property-inspection" },
  { label: "Property Maintenance", href: "/property-maintenance" },
  { label: "Property Make-Ready", href: "/property-make-ready" },
  { label: "Interior & Renovation", href: "/interior-services" },
];

const COMPANY_LINKS = [
  { label: "About Us", href: "/about" },
  { label: "All Services", href: "/services" },
  { label: "Pricing", href: "/pricing" },
  { label: "How It Works", href: "/how-it-works" },
  { label: "NRI Tax & TDS Guide", href: "/nri-tax-tds-guide" },
  { label: "FAQ", href: "/faq" },
  { label: "Contact", href: "/contact" },
];

export const Footer = () => (
  <footer className="bg-navy-950 text-white" data-testid="site-footer">
    <div className="container-site grid gap-12 py-16 sm:py-20 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
      <div>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold-500 font-display text-sm font-extrabold text-navy-900">NRI</div>
          <div className="leading-tight">
            <span className="block font-display text-base font-extrabold tracking-tight">NRI Property Services</span>
            <span className="block text-[9px] font-semibold uppercase tracking-[0.2em] text-white/45">
              A Quality Expertz Group Company
            </span>
          </div>
        </div>
        <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/55">
          Professional property management, property care and local owner representation for NRIs and
          owners living away from Hyderabad. Your property. Our presence. Your peace of mind.
        </p>
        <p className="mt-6 text-xs uppercase tracking-[0.25em] text-gold-500">
          Currently serving Hyderabad & Greater Hyderabad
        </p>
      </div>

      <nav aria-label="Services">
        <h3 className="font-display text-xs font-bold uppercase tracking-[0.28em] text-gold-500">Services</h3>
        <ul className="mt-5 space-y-3">
          {SERVICE_LINKS.map((l) => (
            <li key={l.href}>
              <Link to={l.href} className="text-sm text-white/60 transition-colors hover:text-gold-400" data-testid={`footer-service-${l.href.slice(1)}`}>
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <nav aria-label="Company">
        <h3 className="font-display text-xs font-bold uppercase tracking-[0.28em] text-gold-500">Company</h3>
        <ul className="mt-5 space-y-3">
          {COMPANY_LINKS.map((l) => (
            <li key={l.href}>
              <Link to={l.href} className="text-sm text-white/60 transition-colors hover:text-gold-400" data-testid={`footer-company-${l.href.slice(1)}`}>
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <div>
        <h3 className="font-display text-xs font-bold uppercase tracking-[0.28em] text-gold-500">Contact</h3>
        <ul className="mt-5 space-y-4 text-sm text-white/60">
          <li className="flex items-start gap-3">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" />
            <span>{SITE.location}<br />{SITE.serviceArea}</span>
          </li>
          <li>
            <a href={SITE.phoneHref} onClick={() => track("phone_click", { location: "footer" })} className="flex items-center gap-3 transition-colors hover:text-gold-400" data-testid="footer-phone">
              <Phone className="h-4 w-4 shrink-0 text-gold-500" /> {SITE.phoneDisplay}
            </a>
          </li>
          <li>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { location: "footer" })} className="flex items-center gap-3 transition-colors hover:text-gold-400" data-testid="footer-whatsapp">
              <WhatsAppIcon className="h-4 w-4 shrink-0 text-gold-500" /> WhatsApp: {SITE.whatsappDisplay}
            </a>
          </li>
          <li>
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 transition-colors hover:text-gold-400" data-testid="footer-email">
              <Mail className="h-4 w-4 shrink-0 text-gold-500" /> {SITE.email}
            </a>
          </li>
          <li className="flex items-center gap-3">
            <Globe className="h-4 w-4 shrink-0 text-gold-500" /> {SITE.domain}
          </li>
        </ul>
      </div>
    </div>

    <div className="border-t border-white/10">
      <div className="container-site flex flex-col items-center justify-between gap-4 py-6 text-xs text-white/40 sm:flex-row">
        <p>© 2026 NRI Property Services. All Rights Reserved.</p>
        <nav className="flex gap-6" aria-label="Legal">
          <Link to="/privacy-policy" className="transition-colors hover:text-gold-400" data-testid="footer-privacy">Privacy Policy</Link>
          <Link to="/terms" className="transition-colors hover:text-gold-400" data-testid="footer-terms">Terms & Conditions</Link>
          <Link to="/disclaimer" className="transition-colors hover:text-gold-400" data-testid="footer-disclaimer">Disclaimer</Link>
        </nav>
      </div>
    </div>
  </footer>
);
